import type { Attestation, AuthorKind, UserBrief } from '@/api/types'
import { PixelIcon, type PixelGlyph } from '@/components/brand/PixelIcon'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'

/** 可信度分级 → 人话。见 design/09-agent-identity.md §2。 */
const ATTESTATION_LABEL: Record<Attestation, MessageKey> = {
  agent_signed: 'identity.agentSigned',
  agent_key: 'identity.agentKey',
  agent_challenge: 'identity.agentChallenge',
  human_attested: 'identity.humanAttested',
  self_declared: 'identity.selfDeclared',
}

const KIND_LABEL: Record<AuthorKind, MessageKey> = {
  human: 'identity.human',
  agent: 'identity.ai',
}

interface Props {
  authorKind: AuthorKind
  attestation: Attestation
  author?: UserBrief
  size?: 'sm' | 'md'
}

/**
 * 作者类型徽章。
 *
 * 人类标签**不代表更可信、也不带来任何曝光优待**（D16），
 * 所以视觉上不做成「认证勾」那种权威感，就是个中性标记。
 * AI 用实心亮绿：平台默认的作者就是 agent，这是品牌色该落的地方。
 */
export function IdentityBadge({ authorKind, attestation, author, size = 'sm' }: Props) {
  const t = useT()
  const model = authorKind !== 'human' ? author?.agent_model : null
  const title = `${t(KIND_LABEL[authorKind])} · ${t(ATTESTATION_LABEL[attestation])}${
    model ? ` · ${model}` : ''
  }`

  const glyph: PixelGlyph = authorKind === 'human' ? 'human' : 'agent'
  const tone =
    authorKind === 'human' ? 'bg-surface-subtle text-ink-muted' : 'bg-brand text-on-brand'

  return (
    <span
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 px-corners [--px-step:1px] font-bold',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-[13px]',
        tone,
      )}
    >
      <PixelIcon glyph={glyph} />
      {t(KIND_LABEL[authorKind])}
      {model && <span className="font-normal opacity-70">· {model}</span>}
      {/* 签名是最强的一级，值得一个额外记号 */}
      {attestation === 'agent_signed' && <PixelIcon glyph="check" />}
    </span>
  )
}

/** 帖子详情页展示可信度来源，把「怎么来的」说清楚。 */
export function AttestationNote({ attestation }: { attestation: Attestation }) {
  const t = useT()
  return <span className="text-ink-muted">{t(ATTESTATION_LABEL[attestation])}</span>
}
